import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowUp, Square } from 'lucide-react'
import { useMeta } from '@/api/queries'
import { cn } from '@/lib/cn'
import { spring } from '@/lib/motion'
import { useAutosize } from '@/hooks/useAutosize'
import { useUiStore } from '@/stores/uiStore'
import { DepartmentSelect } from './DepartmentSelect'

type Props = {
  conversationId: string | null
  isStreaming: boolean
  /** Return true if the message was accepted (the draft is then cleared). */
  onSend: (text: string) => boolean
  onStop: () => void
  /** "hero": centred on the welcome screen; "dock": pinned under the conversation. */
  placement?: 'hero' | 'dock'
}

/** Shared-layout id: the composer glides between the welcome screen and the dock. */
export const COMPOSER_LAYOUT_ID = 'composer'

export function Composer({ conversationId, isStreaming, onSend, onStop, placement = 'dock' }: Props) {
  const meta = useMeta()
  const [draft, setDraft] = useState('')
  const [prevConversation, setPrevConversation] = useState(conversationId)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const focusNonce = useUiStore((s) => s.composerFocusNonce)
  const inputId = useId()
  const hintId = useId()
  useAutosize(textareaRef, draft)

  // Switching conversations clears the draft (but a new chat getting its id does not).
  if (prevConversation !== conversationId) {
    setPrevConversation(conversationId)
    if (prevConversation !== null) setDraft('')
  }

  const lastFocusNonce = useRef(focusNonce)
  useEffect(() => {
    const requested = focusNonce !== lastFocusNonce.current
    lastFocusNonce.current = focusNonce
    // Don't pop up the on-screen keyboard on touch devices just for navigating.
    if (!requested && window.matchMedia('(pointer: coarse)').matches) return
    textareaRef.current?.focus()
  }, [focusNonce, conversationId])

  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    if (isStreaming || !draft.trim()) return
    if (onSend(draft)) setDraft('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
    }
  }

  const canSend = Boolean(draft.trim())
  const hero = placement === 'hero'

  return (
    <div className={cn('shrink-0', hero ? 'w-full' : 'relative px-3 pb-3 pt-1 sm:px-6')}>
      {/* fade the conversation out under the dock */}
      {!hero && (
        <div className="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-gradient-to-t from-canvas to-transparent" />
      )}
      <form onSubmit={submit} className="mx-auto max-w-reading">
        <motion.div
          layoutId={COMPOSER_LAYOUT_ID}
          transition={spring.gentle}
          data-glow={isStreaming ? 'on' : 'off'}
          className="relative rounded-[26px]"
        >
          <span className="ai-ring-glow" aria-hidden />
          <span className="ai-ring-border" aria-hidden>
            <span className="ai-ring-spin" />
          </span>
          <div
            className={cn(
              // Solid, not frosted: the glowing ring animates right behind it, and a backdrop blur
              // would have to be recomputed on every frame.
              'relative z-[1] rounded-[26px] bg-surface shadow-composer',
              'transition-shadow duration-300 focus-within:shadow-[0_0_0_4px_rgb(var(--accent)/0.12),0_16px_40px_-16px_rgb(0_0_0/0.2)]',
            )}
          >
            <label htmlFor={inputId} className="sr-only">
              Message {meta.assistant_name}
            </label>
            <textarea
              id={inputId}
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={hero ? 2 : 1}
              aria-describedby={hintId}
              placeholder={hero ? `Ask anything about ${meta.org_name}…` : `Message ${meta.assistant_name}…`}
              className="block max-h-60 w-full resize-none bg-transparent px-5 pb-1 pt-4 text-[15.5px] leading-6 text-ink placeholder:text-ink-faint focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5 pt-1">
              <DepartmentSelect />
              <SendStopButton streaming={isStreaming} canSend={canSend} onStop={onStop} />
            </div>
          </div>
        </motion.div>
        <p id={hintId} className="mt-2.5 px-2 text-center text-[11.5px] leading-5 text-ink-faint">
          Answers come from {meta.org_name} documents and can be incomplete or out of date. Check the sources for
          anything important.
        </p>
      </form>
    </div>
  )
}

/** One round button that morphs between "send" and "stop". */
function SendStopButton({ streaming, canSend, onStop }: { streaming: boolean; canSend: boolean; onStop: () => void }) {
  const active = streaming || canSend
  return (
    <motion.button
      type={streaming ? 'button' : 'submit'}
      onClick={streaming ? onStop : undefined}
      disabled={!active}
      aria-label={streaming ? 'Stop generating' : 'Send message'}
      whileTap={active ? { scale: 0.88 } : undefined}
      animate={{ scale: active ? 1 : 0.94 }}
      transition={spring.snappy}
      className={cn(
        'relative inline-flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full transition-colors duration-200',
        streaming
          ? 'bg-ink text-canvas'
          : canSend
            ? 'bg-accent text-accent-ink shadow-[0_2px_8px_-2px_rgb(var(--accent)/0.6)] hover:bg-accent-hover'
            : 'bg-subtle text-ink-faint',
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {streaming ? (
          <motion.span
            key="stop"
            initial={{ scale: 0.4, opacity: 0, rotate: -90 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.4, opacity: 0, rotate: 90 }}
            transition={spring.snappy}
            className="flex"
          >
            <Square className="size-3.5 fill-current" />
          </motion.span>
        ) : (
          <motion.span
            key="send"
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={spring.snappy}
            className="flex"
          >
            <ArrowUp className="size-[18px]" strokeWidth={2.4} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  )
}
